// #region License

/**
 * @license
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

// #endregion

import * as path from "path";
import * as _ from "lodash";
import * as React from "react";
import { useEffect, useEventCallback } from "@jvs-group/jvs-mairistem-tools";
import * as host from "../../utils/host";
import { loadManifest, unloadManifest } from "../../utils/manifest";
import { ApplicationNavigationContext } from "../ApplicationNavigation";
import { ApplicationSessionContext } from "../ApplicationSession";
import { ApplicationPageContext } from "./ApplicationPageContext";
import { FeatureService } from "../../entities/Feature";
import { MenuService } from "../../entities/Menu";
import { SpeedDialService } from "../../entities/SpeedDial";
import { useAxios } from "../../utils/axios";
import { isUrl } from "../../utils/path";
import { useHistory } from "../../services/router";

// eslint-disable-next-line max-len
import { jsx as _jsx } from "react/jsx-runtime";
export const ApplicationPageContextProvider = props => {
  const {
    children
  } = props;
  const {
    Provider
  } = ApplicationPageContext;
  const {
    user
  } = React.useContext(ApplicationSessionContext);
  const {
    module
  } = React.useContext(ApplicationNavigationContext);
  const [manifest, setManifest] = React.useState();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [service, setService] = React.useState();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const history = useHistory();
  const {
    request,
    response
  } = useAxios(user, module);
  const interceptorRequestId = React.useRef();
  const interceptorResponseId = React.useRef();
  const coerceManifest = useEventCallback((value, identifiant, pathname) => {
    const man = {
      identifiant,
      ...value,
      features: _.map(value.features, feature => ({
        ...feature,
        path: isUrl(feature.path) ? feature.path : path.join(pathname, feature.path)
      })),
      menus: _.map(value.menus, menu => ({
        ...menu,
        path: isUrl(menu.path) ? menu.path : path.join(pathname, menu.path)
      })),
      ...(value.speedDial && {
        speedDial: {
          ...value.speedDial,
          buttons: _.map(value.speedDial?.buttons, button => ({
            ...button,
            path: isUrl(button.path) ? button.path : path.join(pathname, button.path)
          }))
        }
      })
    };
    React.startTransition(() => {
      FeatureService.addFeatureByManifest(man, user);
      MenuService.addMenuByManifest(man, user);
      SpeedDialService.addSpeedDialByManifest(man, user);
    });
    return man;
  });
  const coerceService = useEventCallback((value, identifiant) => {
    const svc = {
      identifiant,
      ...value
    };
    if (!svc.initialized) {
      if (interceptorRequestId.current) {
        svc.request.interceptors.request.eject(interceptorRequestId.current);
      }
      if (interceptorResponseId.current) {
        svc.request.interceptors.response.eject(interceptorResponseId.current);
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      interceptorRequestId.current = svc.request.interceptors.request.use(request.data, request.error);
      svc.request.interceptors.response.clear();
      interceptorResponseId.current = svc.request.interceptors.response.use(response.data, response.error);
      svc.initialized = true;
    }
    return svc;
  });
  const loadModule = React.useCallback((asset, identifiant, hostname, pathname) => {
    const initialize = async mod => {
      const svc = await mod.configure({
        history
      });
      const man = await mod.load(pathname, hostname);
      React.startTransition(() => {
        setService(coerceService(svc, identifiant));
        setManifest(coerceManifest(man, identifiant, pathname));
      });
    };
    if (asset) {
      initialize(asset);
    }
  }, []);
  const unloadModule = React.useCallback(id => {
    React.startTransition(() => {
      setManifest(null);
      setService(null);
      setError(false);
    });
    unloadManifest(id);
  }, []);
  const requireModule = React.useCallback(async (id, hostname) => {
    if (hostname) {
      React.startTransition(() => {
        setLoading(true);
      });
      try {
        return await loadManifest(id, hostname);
      } catch {
        React.startTransition(() => {
          setError(true);
        });
      }
    }
    return null;
  }, []);
  useEffect(() => {
    if (module) {
      requireModule(module.code, host.app(module.repertoire))
      // eslint-disable-next-line max-len
      .then(value => {
        loadModule(value, module.identifiant, host.app(module.repertoire), module.adresse);
      }).catch(() => {
        setError(true);
      }).finally(() => {
        setLoading(false);
      });
    }
    return () => {
      if (module) {
        unloadModule(module.code);
      }
    };
  }, [module]);
  return React.useMemo(() => /*#__PURE__*/_jsx(Provider, {
    value: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      manifest,
      loading,
      error
    },
    children: children
  }), [children, manifest, loading, error]);
};